import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { formatApiError } from '../lib/apiError';
import { LeaveFormDialog } from '../components/leaves/LeaveFormDialog';
import { LeaveCardList } from '../components/leaves/LeaveCardList';
import { LeaveTable } from '../components/leaves/LeaveTable';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyForm = {
  worker_id: '',
  leave_type: '',
  start_date: '',
  end_date: '',
  reason: ''
};

const Leaves = () => {
  const [workers, setWorkers] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [formData, setFormData] = useState(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [workersRes, leavesRes] = await Promise.all([
        axios.get(`${API_URL}/api/workers?active_only=true`),
        axios.get(`${API_URL}/api/leaves`)
      ]);
      setWorkers(workersRes.data);
      setLeaves(leavesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.worker_id || !formData.leave_type || !formData.start_date || !formData.end_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.start_date > formData.end_date) {
      toast.error('End date must be after start date');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/leaves`, formData);
      toast.success('Leave request created');
      setIsDialogOpen(false);
      setFormData(emptyForm);
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to create leave request'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStatusUpdate = async (leaveId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/leaves/${leaveId}`, { status: newStatus });
      toast.success(`Leave ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update leave status');
    }
  };

  const handleDelete = async (leaveId) => {
    if (!window.confirm('Are you sure you want to delete this leave request?')) return;

    try {
      await axios.delete(`${API_URL}/api/leaves/${leaveId}`);
      toast.success('Leave deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete leave');
    }
  };

  const filteredLeaves = statusFilter === 'all'
    ? leaves
    : leaves.filter(l => l.status === statusFilter);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="leaves-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2E4632]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="leaves-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1
            className="text-3xl md:text-4xl font-bold text-[#1C1C1E] tracking-tight"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Leave Management
          </h1>
          <p className="text-[#5C5C5C] mt-1">
            Track and approve worker leaves
          </p>
        </div>

        <LeaveFormDialog
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          workers={workers}
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
        />
      </div>

      <Card className="bg-white border border-[#D1D1CD] rounded-lg shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <Label className="text-sm font-medium text-[#5C5C5C]">Filter by status:</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="leave-status-filter" className="w-40 h-10 border-[#D1D1CD]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <LeaveCardList
        leaves={filteredLeaves}
        onStatusUpdate={handleStatusUpdate}
        onDelete={handleDelete}
      />

      <LeaveTable
        leaves={filteredLeaves}
        onStatusUpdate={handleStatusUpdate}
        onDelete={handleDelete}
      />
    </div>
  );
};

export default Leaves;
